import { useEffect, useRef, useState } from 'react';
import { resetButton } from './ui';
import { imagePoint, maskHasArea, paintRegionGuide, paintRegionMask } from './regionGuide';

/** Internal photo-form editor: local preview only; original is never rewritten. */
export function ImageRegionEditor(props) {
  return <RegionEditorSession key={props.src} {...props} />;
}

function RegionEditorSession({ src, initialStrokes = [], onSave, onCancel }) {
  const canvas = useRef(null);
  const mask = useRef(null);
  const photo = useRef(null);
  const alive = useRef(true);
  const activePointer = useRef(null);
  const history = useRef(initialStrokes);
  const [strokes, setStrokes] = useState(initialStrokes);
  const [ready, setReady] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [hasArea, setHasArea] = useState(false);
  const [erase, setErase] = useState(false);
  const [width, setWidth] = useState(0.06);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const paint = (next, checkArea = true) => {
    if (!photo.current || !mask.current || !canvas.current) return;
    paintRegionMask(mask.current, next);
    paintRegionGuide(canvas.current, photo.current, mask.current);
    if (checkArea) setHasArea(maskHasArea(mask.current));
  };
  const update = (next, checkArea = true) => { history.current = next; setStrokes(next); paint(next, checkArea); };

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (cancelled || !canvas.current) return;
      const scale = Math.min(1, 1536 / Math.max(img.naturalWidth, img.naturalHeight));
      const target = canvas.current;
      target.width = Math.max(1, Math.round(img.naturalWidth * scale));
      target.height = Math.max(1, Math.round(img.naturalHeight * scale));
      mask.current = document.createElement('canvas');
      mask.current.width = target.width; mask.current.height = target.height;
      photo.current = img;
      try { paint(history.current); setReady(true); } catch { setError('Unable to edit this photo. Please select it again.'); }
    };
    img.onerror = () => { if (!cancelled) setError('Unable to open this photo. Please select it again.'); };
    img.src = src;
    return () => { cancelled = true; alive.current = false; img.onload = null; img.onerror = null; };
  }, [src]);

  const point = (event) => {
    const rect = canvas.current.getBoundingClientRect();
    return imagePoint(event.clientX - rect.left, event.clientY - rect.top,
      rect.width, rect.height, canvas.current.width, canvas.current.height);
  };
  const end = (event) => {
    if (event && activePointer.current !== event.pointerId) return;
    activePointer.current = null; setDrawing(false); paint(history.current);
  };
  const save = async () => {
    if (!ready || saving || !hasArea || activePointer.current != null) return;
    setSaving(true); setError('');
    try {
      const blob = await new Promise((resolve, reject) => canvas.current.toBlob(
        (value) => value ? resolve(value) : reject(new Error('Empty export')), 'image/png'));
      if (alive.current) await onSave({ blob, strokes: history.current });
      if (alive.current) setSaving(false);
    } catch {
      if (alive.current) { setError('Unable to save the area. Please try again.'); setSaving(false); }
    }
  };
  return <section aria-label="Mark area" style={{ marginTop: 12, padding: 12, border: '1px solid var(--line)', borderRadius: 16 }}>
    <strong>Mark the area to change</strong>
    <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '6px 0 12px' }}>Brush over the target. Your original photo stays unchanged.</p>
    <canvas ref={canvas} aria-label="Photo region brush" role="img"
      style={{ display: 'block', width: '100%', height: 300, objectFit: 'contain', background: 'var(--surface-2)', touchAction: 'none', borderRadius: 10 }}
      onPointerDown={(event) => {
        if (!ready || saving || activePointer.current != null || event.button > 0) return;
        const p = point(event); if (!p) return;
        activePointer.current = event.pointerId; setDrawing(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
        update([...history.current, { erase, width, points: [p] }]);
      }}
      onPointerMove={(event) => {
        if (activePointer.current !== event.pointerId) return;
        const p = point(event);
        if (!history.current.length) return;
        const next = [...history.current];
        if (p == null && next.at(-1).points.at(-1) == null) return;
        next[next.length - 1] = { ...next.at(-1), points: [...next.at(-1).points, p] };
        update(next, false);
      }} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end} />
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
      <button type="button" style={controlStyle(false, !erase)} disabled={saving || drawing || !ready} aria-pressed={!erase} onClick={() => setErase(false)}>Brush</button>
      <button type="button" style={controlStyle(false, erase)} disabled={saving || drawing || !ready} aria-pressed={erase} onClick={() => setErase(true)}>Erase</button>
      <button type="button" style={controlStyle()} disabled={saving || drawing || !strokes.length} onClick={() => update(history.current.slice(0, -1))}>Undo</button>
      <button type="button" style={controlStyle()} disabled={saving || drawing || !strokes.length} onClick={() => update([])}>Clear</button>
    </div>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>Brush size
      <input type="range" min="0.02" max="0.18" step="0.01" value={width} disabled={saving || drawing} onChange={(e) => setWidth(Number(e.target.value))} style={{ flex: 1, minWidth: 0 }} />
    </label>
    {error && <p role="alert">{error}</p>}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
      <button type="button" style={controlStyle()} onClick={onCancel} disabled={saving || drawing}>Cancel</button>
      <button type="button" style={controlStyle(true)} onClick={save} disabled={!ready || !hasArea || saving || drawing}>{saving ? 'Saving area…' : 'Use area'}</button>
    </div>
  </section>;
}

function controlStyle(primary = false, selected = false) {
  return { ...resetButton, minHeight: 44, padding: '8px 14px', borderRadius: 'var(--r-pill)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--line)'}`,
    background: primary ? 'var(--accent)' : selected ? 'var(--accent-soft)' : 'var(--surface)',
    color: primary ? 'var(--on-accent, white)' : 'var(--text)', fontSize: 14, fontWeight: 600 };
}
